import React, { useState } from "react";
import { Link, NavLink, useLocation, useParams } from "react-router-dom";
import { useI18n } from "../../i18n";
import { Menu, X } from "lucide-react";

const Header = () => {
  const { t, lang } = useI18n();
  const location = useLocation();
  const { lang: urlLang } = useParams();
  const [open, setOpen] = useState(false);

  // Build the URL for the opposite language while preserving the current sub-path
  const buildLangUrl = (target) => {
    const parts = location.pathname.split("/").filter(Boolean);
    if (parts.length === 0) return `/${target}`;
    if (parts[0] === "ec" || parts[0] === "en") {
      parts[0] = target;
    } else {
      parts.unshift(target);
    }
    return "/" + parts.join("/");
  };

  const base = `/${lang}`;

  const navLinks = [
    { href: `${base}#about`, label: t("header.nav.about"), id: "nav-link-about" },
    { href: `${base}#how`, label: t("header.nav.how"), id: "nav-link-how" },
    { href: `${base}#benefits`, label: t("header.nav.benefits"), id: "nav-link-benefits" },
    { href: `${base}#faq`, label: t("header.nav.faq"), id: "nav-link-faq" },
  ];

  const goHome = () => setOpen(false);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 w-full bg-[#0B1120] border-b border-slate-800 shadow-[0_1px_0_0_rgba(148,163,184,0.08)]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link
          to={base}
          onClick={goHome}
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-emerald-600 text-white font-bold font-mono text-sm">
            IA
          </span>
          <span className="flex flex-col leading-tight">
            <span className="text-slate-100 font-semibold tracking-tight text-[15px]">
              {t("common.brand")}
            </span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-emerald-400 font-mono">
              {t("common.brandTagline")}
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="primary">
          {navLinks.map((l) => (
            <a
              key={l.id}
              href={l.href}
              data-testid={l.id}
              className="px-3 py-2 text-sm text-slate-300 hover:text-white rounded-md hover:bg-slate-800"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <div
            className="hidden sm:flex items-center border border-slate-700 rounded-md overflow-hidden"
            role="group"
            aria-label={t("language.label")}
          >
            <Link
              to={buildLangUrl("ec")}
              data-testid="lang-switch-es"
              aria-current={lang === "ec" ? "true" : undefined}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest ${
                lang === "ec"
                  ? "bg-emerald-600 text-white"
                  : "bg-transparent text-slate-300 hover:bg-slate-800"
              }`}
            >
              ES
            </Link>
            <Link
              to={buildLangUrl("en")}
              data-testid="lang-switch-en"
              aria-current={lang === "en" ? "true" : undefined}
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest border-l border-slate-700 ${
                lang === "en"
                  ? "bg-emerald-600 text-white"
                  : "bg-transparent text-slate-300 hover:bg-slate-800"
              }`}
            >
              EN
            </Link>
          </div>

          <a
            href={`${base}#newsletter`}
            data-testid="header-cta-button"
            className="hidden md:inline-flex items-center px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
          >
            {t("header.cta")}
          </a>

          <button
            type="button"
            data-testid="header-mobile-menu-toggle"
            aria-expanded={open}
            aria-label={t("common.menu")}
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-700 text-slate-200 hover:bg-slate-800"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#0B1120] border-t border-slate-800" data-testid="mobile-menu">
          <div className="px-5 py-4 flex flex-col gap-1">
            {navLinks.map((l) => (
              <a
                key={l.id}
                href={l.href}
                data-testid={`${l.id}-mobile`}
                onClick={() => setOpen(false)}
                className="px-3 py-3 text-sm text-slate-200 hover:bg-slate-800 rounded-md"
              >
                {l.label}
              </a>
            ))}
            <a
              href={`${base}#newsletter`}
              data-testid="header-cta-button-mobile"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex items-center justify-center px-4 py-3 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
            >
              {t("header.cta")}
            </a>
            <div className="mt-3 flex items-center gap-2 border-t border-slate-800 pt-3">
              <span className="text-xs uppercase tracking-widest text-slate-400 font-mono">
                {t("language.label")}
              </span>
              <Link
                to={buildLangUrl("ec")}
                data-testid="lang-switch-es-mobile"
                onClick={() => setOpen(false)}
                className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest rounded ${
                  lang === "ec" ? "bg-emerald-600 text-white" : "text-slate-300 border border-slate-700"
                }`}
              >
                ES
              </Link>
              <Link
                to={buildLangUrl("en")}
                data-testid="lang-switch-en-mobile"
                onClick={() => setOpen(false)}
                className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest rounded ${
                  lang === "en" ? "bg-emerald-600 text-white" : "text-slate-300 border border-slate-700"
                }`}
              >
                EN
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
